import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "../lib/supabase";
import type { Profile } from "../lib/supabase";
import type { Session } from "@supabase/supabase-js";
import { cacheOwnerOfflineAuth, verifyOwnerOfflineCredentials, updateOwnerCachedTokens } from "../lib/offlineAuth";

interface AuthContextType {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isOfflineMode: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  fetchProfile: (userId?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  // Guard so clearing stale tokens doesn't re-trigger the handler in a loop
  const clearingRef = useRef(false);
  // Set to true while AuthContext.signIn is running so fetchProfile suppresses admin
  // profile state — prevents PublicRoute from redirecting admin away before the
  // rejection signOut fires (race condition between onAuthStateChange and signIn).
  const ownerSignInRef = useRef(false);

  useEffect(() => {
    // If the URL contains a recovery token, skip getSession entirely.
    // onAuthStateChange will fire PASSWORD_RECOVERY and ResetPassword handles it.
    const isRecovery = window.location.hash.includes("type=recovery");

    if (!isRecovery) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        if (session) fetchProfile(session.user.id);
        else setLoading(false);
      });
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        // Recovery link clicked — stop loading, do NOT set session or profile.
        // ResetPassword page handles everything from here.
        setLoading(false);
        return;
      }

      // Keep cached tokens current after every refresh
      if (session?.access_token && session?.refresh_token) {
        updateOwnerCachedTokens(session.access_token, session.refresh_token);
      }

            // When the refresh token is invalid/expired the SDK emits SIGNED_OUT.
      // BUT if we are offline, don't clear the session — let them keep working!
      if (event === "SIGNED_OUT" && !session && !clearingRef.current) {
        if (navigator.onLine) {
          clearingRef.current = true;
          supabase.auth.signOut({ scope: "local" }).finally(() => { clearingRef.current = false; });
        } else {
          // If offline, we ignore the sign-out so they stay logged in
          return; 
        }
      }

      setSession(session);
      if (session) fetchProfile(session.user.id);
      else { setIsOfflineMode(false); setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

    const fetchProfile = async (userId?: string) => {
    const uid = userId ?? session?.user?.id;
    if (!uid) { setLoading(false); return; }
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", uid)
      .single();
    if (data && (data.role === "supervisor" || data.role === "admin")) {
      // If the owner-portal signIn is still in progress and the role is admin,
      // suppress setting the profile so PublicRoute doesn't redirect before
      // signIn can call signOut and return the error.
      if (data.role === "admin" && ownerSignInRef.current) {
        setLoading(false);
        return;
      }
      setProfile(data as Profile);
      // Tag every future Sentry event with this owner's identity
      Sentry.setUser({
        id:       data.id,
        username: data.name ?? undefined,
        email:    data.internal_email ?? undefined,
      });
    } else {
      setProfile(null);
      Sentry.setUser(null);
    }
    setLoading(false);
  };

  const signIn = async (email: string, password: string) => {
    // ── Offline path ──────────────────────────────────────────────────────
    if (!navigator.onLine) {
      const cached = await verifyOwnerOfflineCredentials(email.trim(), password);
      if (!cached) return { error: "offline_no_cache" };

      try {
        await supabase.auth.setSession({
          access_token:  cached.access_token,
          refresh_token: cached.refresh_token,
        });
      } catch { /* session restore failed — continue with profile-only offline mode */ }

      setProfile(cached.profile);
      setIsOfflineMode(true);
      setLoading(false);
      return { error: null };
    }

    // ── Online path ──────────────────────────────────────────────────────
    // Flag is set so fetchProfile suppresses admin profile state while we verify.
    ownerSignInRef.current = true;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      ownerSignInRef.current = false;
      const msg = error.message.toLowerCase();
      if (msg.includes("banned") || msg.includes("user is banned")) {
        return { error: "__locked__" };
      }
      if (msg.includes("email not confirmed")) {
        return { error: "Please confirm your email first. Check your inbox for the confirmation link." };
      }
      if (msg.includes("too many requests") || msg.includes("rate limit")) {
        return { error: "Too many attempts. Please wait a few minutes and try again." };
      }
      // Generic message for all credential failures — avoids revealing whether
      // the email is registered (user enumeration).
      return { error: "Incorrect email or password. Please try again." };
    }

    // Signed in via auth — verify they have a supervisor profile.
    // Admin accounts are rejected here with the same generic error so the
    // owner portal reveals nothing about the admin portal's existence.
    if (data.user) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", data.user.id)
        .maybeSingle();

      if (!profileData || profileData.role !== "supervisor") {
        ownerSignInRef.current = false;
        await supabase.auth.signOut();
        return { error: profileData?.role === "admin"
          ? "Incorrect email or password. Please try again."
          : "No account found. If you are an agent, use the agent login instead." };
      }

      // Cache credentials for offline login
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await cacheOwnerOfflineAuth(
          email.trim(), password, profileData as Profile,
          session.access_token, session.refresh_token,
        );
      }
    }

    ownerSignInRef.current = false;
    setIsOfflineMode(false);
    return { error: null };
  };

  const signOut = async () => {
    // When offline, skip the server call so local state still clears cleanly
    await supabase.auth.signOut(navigator.onLine ? undefined : { scope: "local" });
    Sentry.setUser(null);
    setProfile(null); setSession(null); setIsOfflineMode(false);
  };

  return (
    <AuthContext.Provider value={{ session, profile, loading, isOfflineMode, signIn, signOut, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};